import { useState } from "react";
import { toast } from "sonner";
import { FileSignature, Trash2 } from "lucide-react";
import { CommercialDownloadButton } from "./DownloadButton";
import { ContractSigningPanel } from "./ContractSigningPanel";
import { RequestChangesPanel } from "./RequestChangesPanel";
import { useDeleteContract } from "@/lib/commercialQueries";
import { SimpleDeleteConfirm } from "../shared/SimpleDeleteConfirm";

const STATUS_STYLES = {
  pending_signature: "bg-mustard/20 text-ink", changes_requested: "bg-terracotta/15 text-terracotta",
  signed: "bg-emerald-100 text-emerald-700", expired: "bg-red-100 text-red-700",
  cancelled: "bg-red-100 text-red-700", superseded: "bg-ink/10 text-ink-soft", draft: "bg-ink/10 text-ink-soft",
};

/**
 * Master v5 Prompt 14 — ContractRecord list/status + the current
 * version's full detail (unsigned/signed downloads, signing panel,
 * Request changes). Older/superseded versions stay downloadable but
 * carry no action. Prompt 30D — a coach (never a client) can
 * permanently delete any contract version, current or historical, at
 * any status (cascades its own signature requests/change
 * requests/comments; never touches the document FileAssets).
 */
export function ContractCard({ workspaceId, contracts, currentContractId, isClientActor, isViewAsClient }) {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const deleteContract = useDeleteContract(workspaceId);

  if (contracts.length === 0) {
    return (
      <div className="border border-ink/10 rounded-lg p-4 bg-paper" data-testid="commercial-contracts-empty">
        <p className="text-sm text-ink-soft italic">No contract has been shared yet.</p>
      </div>
    );
  }
  const current = contracts.find((c) => c.ctr_id === currentContractId) || contracts[0];
  const history = contracts.filter((c) => c.ctr_id !== current.ctr_id);

  async function handleConfirmDelete() {
    try {
      await deleteContract.mutateAsync(deleteTarget.ctr_id);
      toast.success("Contract deleted");
      setDeleteTarget(null);
    } catch (e) {
      toast.error(e.body?.detail || "Couldn't delete that contract");
    }
  }

  return (
    <div className="border border-ink/10 rounded-lg p-4 bg-paper" data-testid="commercial-contracts-card">
      <h2 className="font-serif text-lg text-ink mb-3 flex items-center gap-1.5"><FileSignature size={17} /> Contract</h2>

      <div className="space-y-3" data-testid={`commercial-contract-${current.ctr_id}`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-sm font-medium text-ink">{current.title} <span className="text-ink-soft">· v{current.version_no}</span></p>
            <span className={`inline-block text-[11px] font-semibold rounded-full px-2 py-0.5 mt-1 capitalize ${STATUS_STYLES[current.status]}`} data-testid="commercial-contract-status-badge">
              {current.status.replace("_", " ")}
            </span>
          </div>
          <div className="flex gap-2">
            <CommercialDownloadButton workspaceId={workspaceId} fasId={current.unsigned_document_fas_id} label="Unsigned copy" testId="commercial-contract-unsigned-download" />
            {current.signed_document_fas_id && (
              <CommercialDownloadButton workspaceId={workspaceId} fasId={current.signed_document_fas_id} label="Signed copy" testId="commercial-contract-signed-download" />
            )}
            {!isClientActor && (
              <button onClick={() => setDeleteTarget(current)} title="Delete permanently" data-testid="commercial-contract-delete-current">
                <Trash2 size={14} className="text-ink-soft hover:text-red-600" />
              </button>
            )}
          </div>
        </div>

        <ContractSigningPanel workspaceId={workspaceId} contract={current} isClientActor={isClientActor} isViewAsClient={isViewAsClient} />

        {isClientActor && !isViewAsClient && ["pending_signature", "changes_requested"].includes(current.status) && (
          <RequestChangesPanel workspaceId={workspaceId} contract={current} />
        )}
      </div>

      {history.length > 0 && (
        <div className="mt-4 pt-3 border-t border-ink/10">
          <p className="text-xs font-medium text-ink-soft mb-1.5">Previous versions</p>
          <div className="space-y-1">
            {history.map((c) => (
              <div key={c.ctr_id} className="flex items-center justify-between text-xs" data-testid={`commercial-contract-history-${c.ctr_id}`}>
                <span className="text-ink-soft">{c.title} · v{c.version_no} · <span className="capitalize">{c.status.replace("_", " ")}</span></span>
                <span className="flex items-center gap-2">
                  <CommercialDownloadButton workspaceId={workspaceId} fasId={c.signed_document_fas_id || c.unsigned_document_fas_id} label="Download" testId={`commercial-contract-history-download-${c.ctr_id}`} />
                  {!isClientActor && (
                    <button onClick={() => setDeleteTarget(c)} title="Delete permanently" data-testid={`commercial-contract-delete-${c.ctr_id}`}>
                      <Trash2 size={13} className="text-ink-soft hover:text-red-600" />
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <SimpleDeleteConfirm
        open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete this contract permanently?"
        description={`"${deleteTarget?.title}" (v${deleteTarget?.version_no}) and its signature/change-request history will be permanently removed. The document itself is not affected. This can't be undone.`}
        onConfirm={handleConfirmDelete} isPending={deleteContract.isPending}
        testId="commercial-contract-delete-confirm"
      />
    </div>
  );
}
